import { makeApi } from '@zodios/core';
import { z } from 'zod';
import { errors, apiKeySchema, createLinkSchema, updateLinkSchema, linkSchema } from './schemas/common.schema';

const adminApi = makeApi([
  {
    method: 'post',
    path: '/v1/admin/apikey',
    alias: 'createAdminApiKey',
    description: 'Create a new admin API key (admin only)',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          name: z.string().min(1),
          description: z.string().optional(),
          expiresAt: z.string().datetime().optional(),
        }),
      },
    ],
    response: z.object({
      ...apiKeySchema.shape,
      apiKey: z.string(),
    }),
    errors,
  },
  {
    method: 'get',
    path: '/v1/admin/link',
    alias: 'adminListLinks',
    description: 'Admin: list global links or by tenant, optionally sorted by click count',
    parameters: [
      {
        name: 'query',
        type: 'Query',
        schema: z.object({
          limit: z.coerce.number().int().min(1).max(100).default(50),
          cursor: z.string().optional(),
          clickOrder: z.enum(['asc', 'desc']).optional(),
          tenant: z.string().optional(),
        }),
      },
    ],
    response: z.object({
      data: z.array(
        z.object({
          shortCode: z.string(),
          tenantId: z.string(),
          redirectURL: z.string(),
          title: z.string().optional(),
          description: z.string().optional(),
          isActive: z.boolean(),
          clickCount: z.number(),
          expiresAt: z.number().optional(),
          createdAt: z.number(),
          updatedAt: z.number(),
        })
      ),
      cursor: z.string().optional(),
    }),
    errors,
  },
  {
    method: 'post',
    path: '/v1/admin/link',
    alias: 'adminCreateLink',
    description: 'Admin: create link for a specific tenant',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: createLinkSchema.extend({ tenantId: z.string() }),
      },
    ],
    response: z.object({
      shortCode: z.string(),
      tenantId: z.string(),
      redirectURL: z.string(),
      title: z.string().optional(),
      description: z.string().optional(),
      isActive: z.boolean(),
      clickCount: z.number(),
      createdAt: z.number(),
      updatedAt: z.number(),
    }),
    errors,
  },
  {
    method: 'get',
    path: '/v1/admin/link/:shortCode',
    alias: 'adminGetLink',
    description: 'Admin: get a link by shortCode (no tenant scope)',
    parameters: [
      { name: 'shortCode', type: 'Path', schema: z.string().length(7) },
    ],
    response: linkSchema,
    errors,
  },
  {
    method: 'put',
    path: '/v1/admin/link/:shortCode',
    alias: 'adminUpdateLink',
    description: 'Admin: update link (optionally scoped by tenant)',
    parameters: [
      { name: 'shortCode', type: 'Path', schema: z.string().length(7) },
      { name: 'body', type: 'Body', schema: updateLinkSchema.extend({ tenantId: z.string().optional() }) },
    ],
    response: z.object({ message: z.string() }),
    status: 204,
    errors,
  },
  {
    method: 'delete',
    path: '/v1/admin/link/:shortCode',
    alias: 'adminDeleteLink',
    description: 'Admin: delete link (optionally scoped by tenant)',
    parameters: [
      { name: 'shortCode', type: 'Path', schema: z.string().length(7) },
      { name: 'tenantId', type: 'Query', schema: z.string().optional() },
    ],
    response: z.object({ message: z.string() }),
    status: 204,
    errors,
  },
  {
    method: 'get',
    path: '/v1/admin/apikey',
    alias: 'listAdminApiKeys',
    description: 'List admin API keys (admin only)',
    response: z.array(apiKeySchema),
    errors,
  },

  {
    method: 'patch',
    path: '/v1/admin/apikey/:keyId',
    alias: 'updateAdminApiKey',
    description: 'Update admin API key status (activate/deactivate)',
    parameters: [
      {
        name: 'keyId',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'body',
        type: 'Body',
        schema: z.object({
          isActive: z.boolean(),
        }),
      },
    ],
    response: z.object({ message: z.string() }),
    status: 204,
    errors,
  },
  {
    method: 'delete',
    path: '/v1/admin/apikey/:keyId',
    alias: 'deleteAdminApiKey',
    description: 'Permanently delete an admin API key (admin only)',
    parameters: [
      {
        name: 'keyId',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ message: z.string() }),
    status: 204,
    errors,
  },
  {
    method: 'get',
    path: '/v1/admin/analytics/overview',
    alias: 'getAnalyticsOverview',
    description: 'Get analytics overview (total clicks, top links, tenant summary)',
    response: z.object({
      totalClicks: z.number(),
      totalLinks: z.number(),
      topLinks: z.array(z.object({
        shortCode: z.string(),
        title: z.string().optional(),
        clickCount: z.number(),
        tenantId: z.string(),
        tenantName: z.string().optional(),
      })),
      tenantSummary: z.array(z.object({
        tenantId: z.string(),
        tenantName: z.string(),
        linkCount: z.number(),
        totalClicks: z.number(),
      })),
    }),
    status: 200,
    errors,
  },
]);

export default adminApi;

